import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store/store';
import { inviteUser } from '@/store/invitationsSlice';
import { useToast } from '@/components/ui/toast';
import { useHospital } from '@/hooks/useHospital';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Typography from '@/components/ui/typography';
import { Card, CardContent } from '@/components/ui/card';

interface InviteForm {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  ehrUsername: string;
}

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
}

interface InviteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ehrUsernameLabel: string;
}

const roleOptions = [
  { value: 'BILLER', label: 'Biller' },
  { value: 'CODER', label: 'Coder' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'DOCTOR', label: 'Doctor' },
  { value: 'DOCTOR_ASSISTANT', label: 'Assistant' },
];

const emptyForm: InviteForm = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  ehrUsername: '',
};

// Email validation function that allows organizational addresses
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const validateField = (
  field: keyof InviteForm,
  value: string,
): string | undefined => {
  const trimmedValue = value.trim();

  switch (field) {
    case 'firstName':
      if (!trimmedValue) return 'First name is required';
      if (trimmedValue.length < 2)
        return 'First name must be at least 2 characters';
      if (trimmedValue.length > 50)
        return 'First name must be less than 50 characters';
      if (!/^[a-zA-Z\s-']+$/.test(trimmedValue))
        return 'First name can only contain letters, spaces, hyphens, and apostrophes';
      break;

    case 'lastName':
      if (!trimmedValue) return 'Last name is required';
      if (trimmedValue.length < 2)
        return 'Last name must be at least 2 characters';
      if (trimmedValue.length > 50)
        return 'Last name must be less than 50 characters';
      if (!/^[a-zA-Z\s-']+$/.test(trimmedValue))
        return 'Last name can only contain letters, spaces, hyphens, and apostrophes';
      break;

    case 'email':
      if (!trimmedValue) return 'Email is required';
      if (!validateEmail(trimmedValue))
        return 'Please enter a valid email address';
      break;

    case 'role':
      if (!trimmedValue) return 'Role is required';
      break;
  }

  return undefined;
};

const InviteModal: React.FC<InviteModalProps> = ({
  open,
  onOpenChange,
  ehrUsernameLabel,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { addToast } = useToast();
  const { getSelectedHospitalData } = useHospital();
  const { loading } = useSelector((state: RootState) => state.invitations);

  const [form, setForm] = useState<InviteForm>(emptyForm);
  const [errors, setErrors] = useState<FieldErrors>({});

  const updateField = (field: keyof InviteForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field !== 'ehrUsername') {
      setErrors((prev) => ({
        ...prev,
        [field]: validateField(field, value),
      }));
    }
  };

  const validateAll = (): boolean => {
    const newErrors: FieldErrors = {};
    (['firstName', 'lastName', 'email', 'role'] as const).forEach((field) => {
      const error = validateField(field, form[field]);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setForm(emptyForm);
    setErrors({});
  };

  const handleSendInvite = async () => {
    if (!validateAll()) return;

    const selectedHospitalData = getSelectedHospitalData();
    if (!selectedHospitalData) return;

    try {
      await dispatch(
        inviteUser({
          tenantId: selectedHospitalData.tenantId,
          email: form.email.trim().toLowerCase(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          roles: [form.role],
          ehrUsername: form.ehrUsername.trim() || undefined,
        }),
      ).unwrap();

      addToast({
        message: 'Invitation sent successfully',
        type: 'success',
        duration: 5000,
      });
      onOpenChange(false);
      resetForm();
    } catch (error) {
      addToast({
        message:
          error instanceof Error ? error.message : 'Failed to invite user',
        type: 'error',
        duration: 5000,
      });
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    resetForm();
  };

  const isValid =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.email.trim() &&
    form.role;

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-left">
            <Typography
              variant="h2"
              weight="semibold"
              className="text-neutral-900"
            >
              Invite team member
            </Typography>
          </DialogTitle>
          <Typography variant="small" className="text-neutral-600 mt-2">
            The team member will receive an email to complete registration.
          </Typography>
        </DialogHeader>

        <div className="space-y-4">
          <Card className="border border-[#F3F4F6] bg-[#F7F8FA]">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Typography
                    variant="small"
                    className="text-neutral-700 font-medium"
                  >
                    First Name
                  </Typography>
                  <Input
                    placeholder="Enter first name"
                    value={form.firstName}
                    onChange={(e) => updateField('firstName', e.target.value)}
                    className={errors.firstName ? 'border-red-500' : ''}
                  />
                  {errors.firstName && (
                    <Typography
                      size="12"
                      weight="light"
                      className="text-red-500"
                    >
                      {errors.firstName}
                    </Typography>
                  )}
                </div>

                <div className="space-y-2">
                  <Typography
                    variant="small"
                    className="text-neutral-700 font-medium"
                  >
                    Last Name
                  </Typography>
                  <Input
                    placeholder="Enter last name"
                    value={form.lastName}
                    onChange={(e) => updateField('lastName', e.target.value)}
                    className={errors.lastName ? 'border-red-500' : ''}
                  />
                  {errors.lastName && (
                    <Typography
                      size="12"
                      weight="light"
                      className="text-red-500"
                    >
                      {errors.lastName}
                    </Typography>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Typography
                    variant="small"
                    className="text-neutral-700 font-medium"
                  >
                    Email
                  </Typography>
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && (
                    <Typography
                      size="12"
                      weight="light"
                      className="text-red-500"
                    >
                      {errors.email}
                    </Typography>
                  )}
                </div>
                <div className="space-y-2">
                  <Typography
                    variant="small"
                    className="text-neutral-700 font-medium"
                  >
                    Role
                  </Typography>
                  <Select
                    value={form.role}
                    onValueChange={(value) => updateField('role', value)}
                  >
                    <SelectTrigger
                      className={errors.role ? 'border-red-500' : ''}
                    >
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.role && (
                    <Typography
                      size="12"
                      weight="light"
                      className="text-red-500"
                    >
                      {errors.role}
                    </Typography>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Typography
                    variant="small"
                    className="text-neutral-700 font-medium"
                  >
                    {ehrUsernameLabel}
                  </Typography>
                  <Input
                    placeholder={`Enter ${ehrUsernameLabel}`}
                    value={form.ehrUsername}
                    onChange={(e) => updateField('ehrUsername', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex gap-3">
          <Button
            variant="primary"
            onClick={handleSendInvite}
            disabled={!isValid || loading}
            className="flex-1 sm:flex-none"
          >
            {loading ? 'Sending...' : 'Send invite'}
          </Button>

          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex-1 sm:flex-none"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteModal;
